import { useCallback, useMemo, useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';

interface SessionWarningModalProps {
  isOpen: boolean;
  secondsRemaining: number;
  totalWarningSeconds?: number;
  onExtend: () => void;
  onLogout: () => void;
  onClose?: () => void;
}

export const SessionWarningModal = ({
  isOpen,
  secondsRemaining: initialSeconds,
  totalWarningSeconds = 300,
  onExtend,
  onLogout,
  onClose,
}: SessionWarningModalProps) => {
  // Internal countdown state - this ensures the timer ALWAYS ticks down
  const [displaySeconds, setDisplaySeconds] = useState(initialSeconds);
  const onLogoutRef = useRef(onLogout);

  // Keep onLogout ref updated
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  // Sync displaySeconds when modal opens or initialSeconds changes significantly
  useEffect(() => {
    if (isOpen) {
      setDisplaySeconds(initialSeconds);
    }
  }, [isOpen, initialSeconds]);

  // Internal countdown timer - runs every second when modal is open
  useEffect(() => {
    if (!isOpen) return;

    const interval = window.setInterval(() => {
      setDisplaySeconds((prev) => {
        if (prev <= 1) {
          onLogoutRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(interval);
  }, [isOpen]);

  const formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(Math.max(0, seconds) / 60);
    const secs = Math.max(0, seconds) % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  const progressPercent = useMemo(() => {
    return Math.max(0, Math.min(100, (displaySeconds / totalWarningSeconds) * 100));
  }, [displaySeconds, totalWarningSeconds]);

  const CIRCLE_SIZE = 140;
  const STROKE_WIDTH = 6;
  const RADIUS = (CIRCLE_SIZE - STROKE_WIDTH) / 2;
  const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
  const OFFSET = CIRCUMFERENCE - (progressPercent / 100) * CIRCUMFERENCE;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent size="md" className="tw-px-10 tw-py-10 tw-rounded-2xl">
        <div className="tw-flex tw-flex-col tw-items-center tw-text-center">
          <h2 className="tw-text-3xl tw-font-extrabold tw-text-gray-900 tw-mb-4">
            Session Timeout Warning
          </h2>
          <p className="tw-text-base tw-text-gray-600 tw-max-w-[520px]">
            You've been inactive for a while. For your security, you'll be automatically logged out soon. Time remaining:
          </p>

          <div className="tw-mt-10 tw-mb-10">
            <div
              style={{
                position: 'relative',
                width: CIRCLE_SIZE,
                height: CIRCLE_SIZE,
              }}
            >
              <svg
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: CIRCLE_SIZE,
                  height: CIRCLE_SIZE,
                  transform: 'rotate(-90deg)',
                }}
              >
                <circle
                  cx={CIRCLE_SIZE / 2}
                  cy={CIRCLE_SIZE / 2}
                  r={RADIUS}
                  stroke="#e5e7eb"
                  strokeWidth={STROKE_WIDTH}
                  fill="none"
                />
                <circle
                  cx={CIRCLE_SIZE / 2}
                  cy={CIRCLE_SIZE / 2}
                  r={RADIUS}
                  stroke="#64748b"
                  strokeWidth={STROKE_WIDTH}
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={CIRCUMFERENCE}
                  strokeDashoffset={OFFSET}
                  style={{ transition: 'stroke-dashoffset 1s linear' }}
                />
              </svg>

              <div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  fontSize: '48px',
                  fontFamily:
                    '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif',
                  fontWeight: 300,
                  letterSpacing: '2px',
                  color: '#1f2937',
                  zIndex: 10,
                }}
                className="tw-tabular-nums"
              >
                {formatTime(displaySeconds)}
              </div>
            </div>
          </div>

          <div className="tw-flex tw-justify-center tw-gap-4">
            <Button
              onClick={onExtend}
              className="tw-px-10 tw-py-3 tw-bg-orange-500 hover:tw-bg-orange-600 tw-text-white tw-text-base tw-font-medium tw-rounded-xl"
              autoFocus
            >
              Stay Signed In
            </Button>
            <Button
              onClick={onLogout}
              variant="outline"
              className="tw-px-10 tw-py-3 tw-text-base tw-font-medium tw-rounded-xl"
            >
              Log Out Now
            </Button>
          </div>

          <p className="tw-text-base tw-text-gray-700 tw-mt-6">
            Your work has been auto-saved
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
